<template>
  <div class="kreativwerkstatt-container">
    <div class="d-flex justify-space-between align-end mb-8">
      <div>
        <h2 class="text-h4 font-weight-bold white--text mb-2">Kreativwerkstatt</h2>
        <p class="text-subtitle-1 grey--text max-w-600">
          Tausche dich mit anderen über Praxis-Beispiele aus. Du siehst hier nur Kategorien von Modulen, die du bereits erfolgreich abgeschlossen hast.
        </p>
      </div>
    </div>

    <!-- Masonry Grid of Categories -->
    <v-row v-if="categories.length > 0">
      <v-col cols="12" sm="6" md="4" lg="3" v-for="cat in categories" :key="cat._id">
      <v-card class="category-card rounded-xl overflow-hidden h-100" dark elevation="0" @click="openCategory(cat)">
          <div class="cat-visual" :class="`visual-${cat.tier}`">
            <v-icon size="40" :color="tierColor(cat.tier)" class="cat-icon">{{ cat.icon || "mdi-lightbulb-on-outline" }}</v-icon>
            <v-chip :color="tierColor(cat.tier)" text-color="black" x-small class="tier-chip font-weight-bold">{{ (cat.tier || "").toUpperCase() }}</v-chip>
            <div class="cat-pattern"></div>
          </div>
          <v-card-text class="pt-4 pb-3">
            <h3 class="text-subtitle-1 font-weight-bold white--text mb-2 text-truncate">{{ cat.title }}</h3>
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center text-caption">
                <v-icon small color="#D4AF37" class="mr-1">mdi-message-text-outline</v-icon>
                <span class="gold-text font-weight-bold mr-1">{{ cat.ideaCount || 0 }}</span>
                <span class="grey--text">Beiträge</span>
              </div>
              <v-btn icon small color="#D4AF37">
                <v-icon>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row v-else>
      <v-col class="text-center py-12">
        <v-icon size="64" color="grey darken-3">mdi-lightbulb-off-outline</v-icon>
        <h3 class="text-h6 grey--text mt-4">Noch keine Kategorien freigeschaltet</h3>
        <p class="text-caption grey--text max-w-400 mx-auto mt-2">Schliesse zuerst Module in der Learning Suite ab, um hier mitreden zu können.</p>
        <v-btn outlined color="#D4AF37" class="mt-4 rounded-pill" @click="$router.push('/akademie/suite')">Zur Learning Suite</v-btn>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import api from '@/api';

export default {
  name: "Kreativwerkstatt",
  data() {
    return {
      categories: [] // Only modules where user has progress.completedAt
    };
  },
  mounted() {
    this.loadCategories();
  },
  methods: {
    async loadCategories() {
      try {
        const [modules, progresses] = await Promise.all([
          api.akademie.fetchModules(),
          api.akademie.fetchProgress()
        ]);
        
        if (modules && progresses) {
          this.categories = modules.filter(mod => {
            const prog = progresses.find(p => p.module === mod._id || p.module === mod.id);
            return prog && prog.completedAt;
          });
        }
      } catch (e) {
        console.error("Fehler beim Laden der Kategorien:", e);
      }
    },
    openCategory(cat) {
      this.$router.push(`/akademie/kreativwerkstatt/${cat._id}`);
    },
    tierColor(tier) {
      if (tier === "bronze") return "#CD7F32";
      if (tier === "silber") return "#C0C0C0";
      if (tier === "gold") return "#FFD700";
      return "#D4AF37";
    }
  }
}
</script>

<style scoped>
.kreativwerkstatt-container {
  min-height: 80vh;
}
.max-w-600 { max-width: 600px; }
.max-w-400 { max-width: 400px; }
.position-relative { position: relative; }
.z-1 { z-index: 1; }

.category-card {
  background: rgba(30, 30, 30, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-card:hover {
  transform: translateY(-5px);
  border-color: rgba(212, 175, 55, 0.5);
  box-shadow: 0 10px 20px rgba(0,0,0,0.5);
}

.overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  transition: background 0.3s ease;
}

.category-card:hover .overlay {
  background: rgba(0,0,0,0.2);
}

.bg-darker {
  background: rgba(18, 18, 18, 0.9);
}
.gold-text { color: #d4af37; }
.h-100 { height: 100%; }

.cat-visual {
  position: relative;
  height: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
}
.cat-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0);
  background-size: 12px 12px;
  opacity: 0.5;
  pointer-events: none;
}
.visual-bronze { background: radial-gradient(circle at 30% 30%, rgba(205, 127, 50, 0.22), rgba(20, 20, 20, 0.9)); }
.visual-silber { background: radial-gradient(circle at 30% 30%, rgba(192, 192, 192, 0.18), rgba(20, 20, 20, 0.9)); }
.visual-gold { background: radial-gradient(circle at 30% 30%, rgba(255, 215, 0, 0.2), rgba(20, 20, 20, 0.92)); }
.cat-icon {
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6));
  transition: transform 0.4s ease;
}
.category-card:hover .cat-icon {
  transform: scale(1.12) rotate(-4deg);
}
.tier-chip { letter-spacing: 1px; z-index: 1; }
</style>
